import type {CodeItem} from '../../vendor/src/features/remotion-editor/editor/items/code/code-item-type';
import {WORD_CHANT_TEMPLATE_ID} from '../../vendor/src/features/remotion-editor/editor/items/code/code-item-type';
import {
  wordChantAnyRenderSpecSchema,
  type WordChantRenderSpec,
} from '../../vendor/src/features/playground/word-chant-remotion/schema';
import {
  lastRecordedOutroCueIndex,
  recordedCueDisplayDurationInFrames,
  recordedCueRenderedText,
  recordedVisualStartFrame,
} from '../../vendor/src/features/playground/word-chant-remotion/timeline';

// The template is data-only, but its legacy schemas permit arbitrary URL schemes
// and passthrough fields. Keep the browser-export boundary finite and media-only.
const MAX_SPEC_BYTES = 8 * 1024 * 1024;
const MAX_REMOTE_URL_LENGTH = 8192;
const MAX_DATA_URL_LENGTH = 2 * 1024 * 1024;
// Eight words need at most 36 bound media assets in v1-v7 (images, word audio,
// guide cues, music) and 17 in v8. Leave room for historical extra assets.
const MAX_RENDER_ASSETS = 128;
// Recorded v8 cues are not array-bounded by the upstream source schema.
const MAX_RECORDED_CUES = 1024;
const MAX_SIMULTANEOUS_CUE_OVERLAYS = 32;
const MAX_TEMPLATE_DURATION_FRAMES = 24 * 60 * 15;

function hasBoundedWorkload(spec: unknown): boolean {
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return false;
  const record = spec as Record<string, unknown>;
  if (!Number.isSafeInteger(record.durationFrames) ||
      (record.durationFrames as number) < 1 ||
      (record.durationFrames as number) > MAX_TEMPLATE_DURATION_FRAMES) return false;
  if (!Array.isArray(record.assets) || record.assets.length > MAX_RENDER_ASSETS) return false;
  if (Array.isArray(record.cueSegments) && record.cueSegments.length > MAX_RECORDED_CUES) return false;
  return true;
}

function hasBoundedCueOverlays(spec: WordChantRenderSpec): boolean {
  if (spec.version !== 'word_chant.render_spec.v8') return true;
  const outroStart = spec.scenes.find(scene => scene.kind === 'outro')?.startFrame ?? spec.durationFrames;
  const lastOutroCueIndex = lastRecordedOutroCueIndex(spec.cueSegments, outroStart);
  const events: Array<{frame: number; change: number}> = [];
  spec.cueSegments.forEach((cue, index) => {
    if (!recordedCueRenderedText(cue.text, cue.displayText)) return;
    const start = recordedVisualStartFrame(cue.startFrame);
    const duration = recordedCueDisplayDurationInFrames(
      cue.text, cue.startFrame, cue.endFrame,
      spec.cueSegments[index + 1]?.startFrame,
      index === lastOutroCueIndex ? spec.durationFrames : undefined,
    );
    events.push({frame: start, change: 1}, {frame: start + duration, change: -1});
  });
  events.sort((a, b) => a.frame - b.frame || a.change - b.change);
  let active = 0;
  for (const event of events) {
    active += event.change;
    if (active > MAX_SIMULTANEOUS_CUE_OVERLAYS) return false;
  }
  return true;
}

function isBoundedMediaUrl(url: string, type: 'image' | 'audio'): boolean {
  if (url.length > MAX_DATA_URL_LENGTH) return false;
  if (url.startsWith('data:')) {
    const mime = type === 'image' ? '(?:png|jpeg|webp)' : '(?:mpeg|mp3|wav|wave|ogg|mp4)';
    return new RegExp(`^data:${type}/${mime};base64,[A-Za-z0-9+/]+={0,2}$`, 'i').test(url);
  }
  if (url.length > MAX_REMOTE_URL_LENGTH) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' &&
      !!parsed.hostname && !parsed.username && !parsed.password;
  } catch {
    return false;
  }
}

/** One trust decision shared by the original renderer and browser export. */
export function readTrustedWordChantSpec(
  item: Pick<CodeItem, 'templateId' | 'wordChantSpec'>,
): WordChantRenderSpec | null {
  if (item.templateId !== WORD_CHANT_TEMPLATE_ID) return null;
  if (!hasBoundedWorkload(item.wordChantSpec)) return null;
  let serialized: string;
  try {
    serialized = JSON.stringify(item.wordChantSpec);
  } catch {
    return null;
  }
  if (!serialized || new TextEncoder().encode(serialized).byteLength > MAX_SPEC_BYTES) return null;
  const parsed = wordChantAnyRenderSpecSchema.safeParse(item.wordChantSpec);
  if (!parsed.success) return null;
  if (parsed.data.assets.some(asset => !isBoundedMediaUrl(asset.url, asset.type))) return null;
  if (!hasBoundedCueOverlays(parsed.data)) return null;
  return parsed.data;
}

export function hasWordChantAudio(spec: WordChantRenderSpec): boolean {
  return spec.assets.some(asset => asset.type === 'audio');
}
